import { useEffect } from 'react';
import { player } from '@/lib/player.ts';
import { pushKeyLayer, type NavKey } from '@/lib/keys.ts';
import { isModalOpen } from '@/ui/Focusable.tsx';
import { wakeChrome, isChromeHidden } from '@/lib/chrome.ts';
import { useUi } from '@/stores/ui.ts';
import { stepChannel } from '@/stores/lineup.ts';

/**
 * What the remote means while video is on screen.
 *
 * This is the layer that sits on top of spatial navigation (see lib/keys.ts).
 * Over full-screen video the grid's meaning of the arrows is wrong: there is
 * nothing beside the picture to move to, and what people reach for instead —
 * every set-top box has trained them — is channels.
 *
 *   chrome hidden   any key just brings the chrome back, and does nothing else
 *   left / right    previous / next channel in the lineup you tuned from
 *   up              the recently-watched strip
 *   down            move focus into the player controls
 *   OK              play / pause
 *   back            down one presentation tier
 *
 * "Any key just wakes it" is not a detail. A control you cannot see is a
 * control you cannot aim at, so a press that both reveals the bar AND fires
 * whatever happened to be under it is a press the user did not consent to.
 */

/** Holding a direction walks the lineup faster than any stream can start. */
const ZAP_SETTLE_MS = 400;

/**
 * Run once the browser has actually drawn.
 *
 * Two frames, not one: a class coming off through React state changes the
 * style in the next frame and the layout it causes in the one after. Focusing
 * anything before that is a no-op against an element that is still hidden.
 */
function afterPaint(fn: () => void): void {
  requestAnimationFrame(() => requestAnimationFrame(fn));
}

export function usePlayerKeys(immersive: boolean): void {
  useEffect(() => {
    if (!immersive) return;

    // Focus is probably still on the grid card that was clicked, which is now
    // hidden behind the video. Drop it: nothing on screen should look focused
    // until the user asks for the controls.
    (document.activeElement as HTMLElement | null)?.blur?.();

    let zapTimer: ReturnType<typeof setTimeout> | null = null;
    let zapFromId: string | null = null;

    /*
     * The FIRST control there is, not a named one.
     *
     * This asked for `mp-toggle` by name, which worked only as long as that
     * button existed on every platform — and it stopped being true when the
     * television dropped Expand and Exit full screen, because the set of
     * controls became something that varies. Asking the bar what it has
     * cannot go stale the next time the controls change.
     */
    /*
     * IS THE CURSOR IN THE CHROME? ASK THE DOM.
     *
     * This was a `chromeFocused` boolean kept in step by hand, and it is the
     * reason this file kept producing bugs. It was set when we focused a
     * control and cleared when we blurred one — but focus also moves for
     * reasons this layer never sees: a dialog opening and closing, a control
     * unmounting when the tier changes, the focus memory restoring a cursor,
     * React re-rendering the bar. Every one of those left the flag saying one
     * thing and the screen showing another, and the symptom was always the
     * same: the remote doing nothing, or doing something somewhere else.
     *
     * The DOM already knows. It cannot drift from itself.
     */
    const cursorInChrome = () =>
      !!(document.activeElement as HTMLElement | null)?.closest?.('.miniplayer, .recents');

    const focusChrome = () => {
      const bar = document.querySelector<HTMLElement>('.miniplayer');
      const first = bar?.querySelector<HTMLElement>('.controls [data-nav]')
        ?? bar?.querySelector<HTMLElement>('[data-nav]');
      first?.focus();
      return !!first;
    };

    const zap = (delta: number) => {
      const from = zapFromId ?? player.getState().channel?.id ?? null;
      const target = stepChannel(from, delta);
      if (!target) return;

      zapFromId = target.id;
      useUi.getState().setZapping(target);

      if (zapTimer) clearTimeout(zapTimer);
      zapTimer = setTimeout(() => {
        zapTimer = null;
        zapFromId = null;
        useUi.getState().setZapping(null);
        // Failures here are already reflected in player state; there is no
        // second thing to tell the user.
        void player.play(target).catch(() => { /* surfaced by the player */ });
      }, ZAP_SETTLE_MS);
    };

    const remove = pushKeyLayer((key: NavKey): boolean => {
      /*
       * A DIALOG IS ON TOP. STAND DOWN.
       *
       * This layer claims the arrow keys whenever something is playing —
       * left and right change channel, up opens the recents strip, and DOWN
       * focuses the player chrome. With the account menu open that last one
       * put the cursor on the now-playing bar BEHIND the dialog: "Switch
       * profile" was highlighted, one press of down landed on something in
       * the background, and the next press activated it.
       *
       * The modal's own layer deliberately returns false for arrows so that
       * spatial navigation can move within it — and this layer sits between
       * the two, so it saw them first. It has no business doing anything to
       * a screen the person cannot see.
       *
       * It surfaced now because the mini player finally renders for library
       * items; before, a film playing left nothing for focusChrome() to land
       * on and the bug had nowhere to go.
       */
      if (isModalOpen()) return false;

      /*
       * Waking the chrome must not eat the press that asked for it.
       *
       * The rule here was "any key just wakes it", to stop a press both
       * revealing the bar and firing whatever happened to be underneath —
       * which is right for OK, and for left and right, since those change
       * channel. It is wrong for DOWN, whose ONLY meaning is "show me the
       * controls": spending the first press waking the bar and the second
       * reaching it reads, from the couch, as the button doing nothing at
       * all. Nothing is activated by moving a cursor, so there is nothing to
       * consent to.
       */
      if (isChromeHidden() && key !== 'back') {
        wakeChrome();
        // DOWN's only meaning is "show me the controls", so it should arrive
        // at them. Deferred because hidden chrome is `visibility: hidden` and
        // focus() on one of those does nothing, silently — and the class
        // comes off through React state, a render away.
        if (key === 'down') afterPaint(focusChrome);
        return true;
      }
      wakeChrome();

      const ui = useUi.getState();

      if (ui.recentsOpen) {
        // Inside the strip the cards are ordinary focusables, so left/right
        // and OK belong to spatial navigation. Only leaving is ours.
        if (key === 'up' || key === 'back') { ui.setRecentsOpen(false); return true; }
        return false;
      }

      /*
       * The cursor is in the chrome, so the chrome's buttons are ordinary
       * focusables and spatial navigation owns them — the same rule as every
       * other screen in this app. Only LEAVING is ours.
       */
      if (cursorInChrome()) {
        if (key === 'up' || key === 'back') {
          (document.activeElement as HTMLElement | null)?.blur?.();
          return true;
        }
        return false;
      }

      switch (key) {
        case 'back':   useUi.getState().stepBack(); return true;
        /*
         * Channel surfing belongs to CHANNELS; on a film these SEEK.
         *
         * Same two keys, two meanings, decided by what is playing — which
         * sounds like a trap and is actually the reflex people already have.
         * On a cable box left and right change channel. On anything with a
         * timeline they move along it. Nobody has ever had to be told this.
         *
         * Ten seconds because that is what every player settled on: long
         * enough to clear a missed line, short enough to press twice without
         * thinking about it.
         */
        case 'left':
          if (player.seekable()) { player.seekBy(-10); return true; }
          if (!player.getState().channel) return false;
          zap(-1); return true;
        case 'right':
          if (player.seekable()) { player.seekBy(10); return true; }
          if (!player.getState().channel) return false;
          zap(1); return true;
        case 'up':
          /*
           * Opening the strip should also put the cursor in it, for the same
           * reason DOWN lands on the controls: a row that appears with
           * nothing focused makes the next press a lottery — spatial
           * navigation, finding no cursor, takes the first candidate in
           * document order, which is somewhere else entirely.
           */
          ui.setRecentsOpen(true);
          afterPaint(() => {
            document.querySelector<HTMLElement>('.recents-row [data-nav]')?.focus();
          });
          return true;
        case 'down':   focusChrome(); return true;
        case 'select': {
          const { status } = player.getState();
          // After a failure OK means "try again" — the most obvious thing to
          // press, and the only thing worth doing.
          if (status === 'error') player.retry();
          else if (status === 'playing') player.pause();
          else player.resume();
          return true;
        }
        default: return false;
      }
    });

    return () => {
      remove();
      if (zapTimer) clearTimeout(zapTimer);
      useUi.getState().setZapping(null);
    };
  }, [immersive]);
}
